import React, { useMemo, useState } from "react";
import { Card, Select, Table, Button } from "antd";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { ApiManager, Locale } from "../../utils";
import { defaultDateRange, buildOrderListUrl, formatMoneyFen } from "../../utils/statistics/statisticsUtils";
import useStatisticsQuery from "./hooks/useStatisticsQuery";
import StatisticsDateFilter from "./components/StatisticsDateFilter";
import StatCardGrid, { buildMoneyCard } from "./components/StatCardGrid";
import TrendChart from "./components/TrendChart";
import PeriodCompare from "./components/PeriodCompare";
import ExportCsvButton from "./components/ExportCsvButton";
import "./statisticsShared.scss";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();

const STATUS_OPTIONS = ["all", "completed", "canceled", "refund"];
const PAYTYPE_OPTIONS = ["all", "wechat", "balance", "cash"];

function OrderStatistics({ user }) {
  const [dateRange, setDateRange] = useState(defaultDateRange(7));
  const [statusGroup, setStatusGroup] = useState("all");
  const [paytype, setPaytype] = useState("all");

  const filters = useMemo(() => {
    const params = {};
    if (statusGroup !== "all") params.status_group = statusGroup;
    if (paytype !== "all") params.paytype = paytype;
    return params;
  }, [statusGroup, paytype]);

  const { loading, data } = useStatisticsQuery({
    endpoint: api.statistics.orders,
    token: user?.token,
    dateRange,
    filters,
  });

  const summary = data?.summary || {};
  const comparison = data?.comparison || {};
  const breakdown = data?.breakdown?.by_paytype || [];
  const range = data?.range || {};

  const statCards = [
    { key: "orders", title: i18n.t("statisticsOrders.ordersCount"), value: summary.orders_count ?? 0, suffix: i18n.t("statisticsOverview.orderUnit") },
    buildMoneyCard("turnover", i18n.t("statisticsOrders.turnover"), summary.turnover_fen),
    buildMoneyCard("income", i18n.t("statisticsOrders.merchantIncome"), summary.merchant_income_fen),
    buildMoneyCard("avg", i18n.t("statisticsOrders.avgOrder"), summary.avg_order_fen),
  ];

  const compareItems = [
    { key: "orders_count", label: i18n.t("statisticsOrders.ordersCount"), data: comparison.orders_count },
    { key: "turnover_fen", label: i18n.t("statisticsOrders.turnover"), data: comparison.turnover_fen, isMoney: true },
    { key: "merchant_income_fen", label: i18n.t("statisticsOrders.merchantIncome"), data: comparison.merchant_income_fen, isMoney: true },
  ];

  const breakdownColumns = [
    { title: i18n.t("statisticsOrders.paytype"), dataIndex: "key", render: (v) => i18n.t(`statisticsOrders.paytypes.${v}`) },
    { title: i18n.t("statisticsOrders.ordersCount"), dataIndex: "count" },
    { title: i18n.t("statisticsOrders.amount"), dataIndex: "amount_fen", render: formatMoneyFen },
  ];

  const drillDownUrl = range.start_date && range.end_date
    ? buildOrderListUrl(range.start_date, range.end_date)
    : "/main/order/list";

  return (
    <div className="statistics-page">
      <div className="statistics-page__header">
        <h2>{i18n.t("statisticsOrders.title")}</h2>
        <Link to={drillDownUrl}>
          <Button type="link">{i18n.t("statisticsCommon.viewOrders")}</Button>
        </Link>
      </div>

      <StatisticsDateFilter value={dateRange} onChange={setDateRange} loading={loading} />

      <Card className="statistics-page__section">
        <div style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 160, marginRight: 8 }}
            value={statusGroup}
            onChange={setStatusGroup}
            options={STATUS_OPTIONS.map((v) => ({ value: v, label: i18n.t(`statisticsOrders.statusGroups.${v}`) }))}
          />
          <Select
            style={{ width: 160 }}
            value={paytype}
            onChange={setPaytype}
            options={PAYTYPE_OPTIONS.map((v) => ({ value: v, label: i18n.t(`statisticsOrders.paytypes.${v}`) }))}
          />
        </div>
        <StatCardGrid items={statCards} loading={loading} />
      </Card>

      <PeriodCompare items={compareItems} />

      <Card title={i18n.t("statisticsCommon.trend")} className="statistics-page__section">
        <TrendChart
          loading={loading}
          data={data?.trend}
          series={[
            { field: "orders_count", name: i18n.t("statisticsOrders.ordersCount"), color: "#1677ff" },
            { field: "merchant_income_fen", name: i18n.t("statisticsOrders.merchantIncome"), color: "#52c41a" },
          ]}
        />
      </Card>

      <Card
        title={i18n.t("statisticsOrders.paytypeBreakdown")}
        className="statistics-page__section"
        extra={
          <ExportCsvButton
            filename="order-paytype-breakdown.csv"
            columns={breakdownColumns}
            rows={breakdown}
          />
        }
      >
        <Table rowKey="key" loading={loading} columns={breakdownColumns} dataSource={breakdown} pagination={false} size="middle" />
      </Card>
    </div>
  );
}

export default connect((state) => ({ user: getUserInfo(state) }))(OrderStatistics);
